/**
 * Custom hook to handle unsaved changes navigation blocking
 * Provides browser and in-app navigation protection with modal confirmation
 */

import { useEffect, useRef, useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface PendingNavigation {
  path: string | number;
  state?: Record<string, unknown>;
}

interface UseUnsavedChangesPromptReturn {
  showLeaveModal: boolean;
  pendingNavigation: PendingNavigation | null;
  handleNavigation: (path: string | number) => void;
  handleNavigationWithState: (path: string | number, state?: Record<string, unknown>) => void;
  handleLeavePage: () => void;
  handleCancelLeave: () => void;
}

/**
 * Hook to manage unsaved changes prompt functionality
 * 
 * @param hasUnsavedChanges - Whether there are unsaved changes
 * @param onConfirmLeave - Optional callback when user confirms leaving with unsaved changes
 * @returns Object containing modal state and navigation handlers
 * 
 * @example
 * ```tsx
 * const {
 *   showLeaveModal,
 *   handleNavigation,
 *   handleLeavePage,
 *   handleCancelLeave,
 * } = useUnsavedChangesPrompt(hasUnsavedChanges);
 * ```
 */
export const useUnsavedChangesPrompt = (
  hasUnsavedChanges: boolean,
  onConfirmLeave?: () => void,
): UseUnsavedChangesPromptReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  
  const shouldBlockRef = useRef(false);
  const isNavigatingRef = useRef(false);
  const locationRef = useRef(location.pathname);

  // Update refs when unsaved changes state changes
  useEffect(() => {
    shouldBlockRef.current = hasUnsavedChanges;
    locationRef.current = location.pathname;
  }, [hasUnsavedChanges, location.pathname]);

  /**
   * Block browser navigation (close/refresh) when there are unsaved changes
   */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlockRef.current) {
        e.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  /**
   * Intercept all link clicks globally to check for unsaved changes
   */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!shouldBlockRef.current || isNavigatingRef.current) {
        return;
      }

      const target = (e.target as HTMLElement).closest(
        'a, button[data-analytics-type="link"]',
      );
      if (!target) return;

      const href = target.getAttribute("href");
      const isExternalLink = href?.startsWith("http");
      const isHashLink = href?.startsWith("#");
      const isSamePage = href === location.pathname;

      if (!href || isExternalLink || isHashLink || isSamePage) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      setPendingNavigation({ path: href });
      setShowLeaveModal(true);
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [location.pathname]);

  /**
   * Navigate with state support
   */
  const handleNavigationWithState = useCallback(
    (path: string | number, state?: Record<string, unknown>) => {
      if (!hasUnsavedChanges) {
        if (typeof path === "number") {
          navigate(path);
        } else if (state) {
          navigate(path, { state });
        } else {
          navigate(path);
        }
        return;
      }

      setPendingNavigation({ path, state });
      setShowLeaveModal(true);
    },
    [hasUnsavedChanges, navigate],
  );

  /**
   * Navigate without state
   */
  const handleNavigation = useCallback(
    (path: string | number) => {
      handleNavigationWithState(path);
    },
    [handleNavigationWithState],
  );

  /**
   * Confirm leaving page with unsaved changes
   */
  const handleLeavePage = useCallback(() => {
    setShowLeaveModal(false);
    isNavigatingRef.current = true;

    if (pendingNavigation) {
      const { path, state } = pendingNavigation;

      if (typeof path === "number") {
        navigate(path);
      } else if (state) {
        navigate(path, { state });
      } else {
        navigate(path);
      }

      setPendingNavigation(null);

      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 100);
    } else {
      isNavigatingRef.current = false;
    }

    onConfirmLeave?.();
  }, [pendingNavigation, navigate, onConfirmLeave]);

  /**
   * Cancel leaving page
   */
  const handleCancelLeave = useCallback(() => {
    setShowLeaveModal(false);
    setPendingNavigation(null);
  }, []);

  return {
    showLeaveModal,
    pendingNavigation,
    handleNavigation,
    handleNavigationWithState,
    handleLeavePage,
    handleCancelLeave,
  };
};
